import React, { useCallback, useState, useRef, useEffect } from 'react';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    Panel,
    Handle,
    Position
} from '@xyflow/react';
import { useTheme } from 'contexts/ThemeContext';
import {
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Select,
    MenuItem,
    Button,
    FormControl,
    InputLabel,
    TextField
} from '@mui/material';
import { Maximize2, Minimize2, Plus, X, Trash } from 'lucide-react';

// Utility to get request_type color
const getMethodColor = (request_type) => {
    switch (request_type) {
        case 'GET': return '#10B981'; // green
        case 'POST': return '#F59E0B'; // amber
        case 'PUT': return '#3B82F6'; // blue
        case 'DELETE': return '#EF4444'; // red
        case 'PATCH': return '#8B5CF6'; // violet
        case 'HEAD': return '#10B981'; // green
        case 'OPTIONS': return '#A78BFA'; // purple
        default: return '#6B7280'; // gray
    }
};

const MethodNode = ({ data, id, handleDeleteNode }) => (
    <div
        style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            padding: 8,
            borderRadius: 2,
            background: 'var(--background-secondary)',
            border: '1px solid var(--borderColor, #e5e7eb)',
            maxWidth: 200,
            width: 200,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            position: 'relative',
            overflow: 'hidden',
        }}
    >
        <Handle type="target" position={Position.Top} style={{ background: '#bbb', left: '50%', transform: 'translateX(-50%)' }} />
        <Handle type="source" position={Position.Bottom} style={{ background: '#bbb', left: '50%', transform: 'translateX(-50%)' }} />

        <span style={{
            color: getMethodColor(data.request_type),
            fontWeight: 600,
            marginRight: 8,
            fontSize: 12,
            letterSpacing: 1,
            flexShrink: 0,
        }}>
            {data.request_type}
        </span>
        <Tooltip title={<span className='text-sm break-words'>{data.label} - {data.url}</span>} placement="top" arrow>
            <span style={{
                color: 'var(--text-primary)',
                fontWeight: 500,
                fontSize: 12,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'inline-block',
                maxWidth: 160,
                verticalAlign: 'middle',
                cursor: 'pointer',
            }}>
                {data.label}
            </span>
        </Tooltip>
        <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); handleDeleteNode(id); }}
            sx={{ marginLeft: 'auto', color: 'var(--text-secondary, #888)' }}
        >
            <Trash size={14} />
        </IconButton>
    </div>
);

const ApiFlowGraph = ({ dependencyGraph, endpoints = [], handleUpdateApiDependencies = () => { } }) => {
    const { isDarkMode } = useTheme();
    const containerRef = useRef(null);
    const dialogRef = useRef(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [updatedDependencyGraph, setUpdatedDependencyGraph] = useState([]);
    const [showAddNodeModal, setShowAddNodeModal] = useState(false);
    const [newNodeApiId, setNewNodeApiId] = useState("");
    const [newNodeTitle, setNewNodeTitle] = useState("");
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);
    const [editApiId, setEditApiId] = useState("");
    const [pendingNodeId, setPendingNodeId] = useState(null);
    const [editTitle, setEditTitle] = useState("");

    // Add effect to handle click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dialogRef.current && !dialogRef.current.contains(event.target)) {
                event.stopPropagation();
                event.preventDefault();
            }
        };

        if (showEditModal) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showEditModal]);

    // Handle fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isFullscreenNow = !!document.fullscreenElement;
            setIsFullscreen(isFullscreenNow);
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('msfullscreenchange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('msfullscreenchange', handleFullscreenChange);
        };
    }, []);

    // Handle Fullscreen API
    const handleToggleFullscreen = () => {
        if (!isFullscreen) {
            const el = containerRef.current;
            if (el) {
                try {
                    if (el.requestFullscreen) el.requestFullscreen();
                    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
                    else if (el.msRequestFullscreen) el.msRequestFullscreen();
                } catch (error) {
                    console.error('Error entering fullscreen:', error);
                }
            }
        } else {
            try {
                if (document.exitFullscreen) document.exitFullscreen();
                else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
                else if (document.msExitFullscreen) document.msExitFullscreen();
            } catch (error) {
                console.error('Error exiting fullscreen:', error);
            }
        }
    };

    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    useEffect(() => {
        const initialNodes = dependencyGraph.map((api, index) => ({
            id: (api.id).toString(),
            data: {
                label: api.name,
                request_type: api.request_type,
                url: api.url,
                title: api.title,
            },
            type: 'methodNode',
            position: { x: 0, y: index * 150 },
            sourcePosition: 'bottom',
            targetPosition: 'top',
        }));

        const initialEdges = dependencyGraph.slice(1).map((api, index) => ({
            id: `e${index}`,
            source: (dependencyGraph[index].id).toString(),
            target: (api.id).toString(),
            animated: true,
            style: { stroke: 'var(--primary-color)' }
        }));

        setNodes(initialNodes);
        setEdges(initialEdges);
        setUpdatedDependencyGraph(dependencyGraph);
    }, [dependencyGraph]);

    const onLayout = useCallback(() => {
        // You can implement custom layout logic here if needed
    }, []);

    const handleNodeClick = (event, node) => {
        event.stopPropagation();
        event.preventDefault();
        if (node.data.isPending) {
            setShowAddNodeModal(true);
            setNewNodeApiId("");
            setNewNodeTitle("");
            setPendingNodeId(node.id);
        } else {
            setSelectedNode(node);
            setEditApiId(node.id); // node.id is id
            setEditTitle(node.data.title);
            setShowEditModal(true);
        }
    };

    const handleEditApiChange = (newApiId) => {
        setEditApiId(newApiId);
    };

    const handleEditTitleChange = (e) => {
        setEditTitle(e.target.value);
    };

    const handleSaveEdit = () => {
        if (!selectedNode || !editApiId) return;
        const selectedEndpoint = endpoints.find(api => api.id?.toString() === editApiId);
        if (!selectedEndpoint) return;
        const mappedApi = {
            id: selectedEndpoint.id,
            name: selectedEndpoint.name,
            url: selectedEndpoint.api_endpoint,
            request_type: selectedEndpoint.request_type,
            title: editTitle,
        };
        // Update node visually
        setNodes(prevNodes => prevNodes.map(node =>
            node.id === selectedNode.id
                ? {
                    ...node,
                    id: mappedApi.id.toString(),
                    data: {
                        label: mappedApi.name ?? '',
                        request_type: mappedApi.request_type ?? '',
                        url: mappedApi.url ?? '',
                        title: mappedApi.title,
                    },
                    type: 'methodNode',
                }
                : node
        ));
        // Update dependency graph
        const newDepGraph = updatedDependencyGraph.map(api =>
            api.id?.toString() === selectedNode.id
                ? { ...api, ...mappedApi }
                : api
        );
        setUpdatedDependencyGraph(newDepGraph);
        handleUpdateApiDependencies(newDepGraph);
        setShowEditModal(false);
        setSelectedNode(null);
        setEditApiId("");
        setEditTitle("");
    };

    const handleCancelEdit = () => {
        setShowEditModal(false);
        setSelectedNode(null);
        setEditApiId("");
        setEditTitle("");
    };

    const handleAddNode = () => {
        setShowAddNodeModal(true);
        setNewNodeApiId("");
        setNewNodeTitle("");
    };

    const handleSaveNewNode = () => {
        const selectedEndpoint = endpoints.find(api => api.id?.toString() === newNodeApiId);
        if (!selectedEndpoint) return;
        const mappedApi = {
            id: selectedEndpoint.id,
            name: selectedEndpoint.name,
            url: selectedEndpoint.api_endpoint,
            request_type: selectedEndpoint.request_type,
            title: newNodeTitle,
        };
        // Add new node to the end
        const newDepGraph = [...updatedDependencyGraph, mappedApi];
        setUpdatedDependencyGraph(newDepGraph);
        // Add node visually
        setNodes(prevNodes => [
            ...prevNodes,
            {
                id: mappedApi.id.toString(),
                data: {
                    label: mappedApi.name ?? '',
                    request_type: mappedApi.request_type ?? '',
                    url: mappedApi.url ?? '',
                    title: mappedApi.title,
                },
                type: 'methodNode',
                position: { x: 0, y: prevNodes.length * 150 },
                sourcePosition: 'bottom',
                targetPosition: 'top',
            }
        ]);
        // Add edge from previous last node if any
        if (nodes.length > 0) {
            setEdges(prevEdges => [
                ...prevEdges,
                {
                    id: `e${nodes.length - 1}-${nodes.length}`,
                    source: nodes[nodes.length - 1].id,
                    target: mappedApi.id.toString(),
                    animated: true,
                    style: { stroke: 'var(--primary-color)' }
                }
            ]);
        }
        handleUpdateApiDependencies(newDepGraph);
        setShowAddNodeModal(false);
    };

    // Add delete handler
    const handleDeleteNode = (nodeId) => {
        setNodeToDelete(nodeId);
        setShowDeleteDialog(true);
    };

    const confirmDeleteNode = () => {
        if (!nodeToDelete) return;
        // Remove node from nodes and dependency graph
        setNodes(prevNodes => prevNodes.filter(node => node.id !== nodeToDelete));
        const newDepGraph = updatedDependencyGraph.filter(api => api.id?.toString() !== nodeToDelete);
        setUpdatedDependencyGraph(newDepGraph);
        handleUpdateApiDependencies(newDepGraph);
        // Remove edges connected to this node
        setEdges(prevEdges => prevEdges.filter(edge => edge.source !== nodeToDelete && edge.target !== nodeToDelete));
        setShowDeleteDialog(false);
        setNodeToDelete(null);
    };

    // Get a set of ids already present in the dependency graph
    const usedApiIds = new Set(updatedDependencyGraph.map(api => api.id?.toString()));

    return (
        <>
            <div
                ref={containerRef}
                style={{
                    position: isFullscreen ? 'fixed' : 'relative',
                    top: isFullscreen ? 0 : undefined,
                    left: isFullscreen ? 0 : undefined,
                    width: isFullscreen ? '100vw' : '100%',
                    height: isFullscreen ? '100vh' : 'calc(100vh - 200px)',
                    zIndex: isFullscreen ? 1300 : 'auto',
                    background: 'var(--background-primary)',
                    boxShadow: isFullscreen ? 'none' : '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                    transition: 'all 0.2s',
                }}
            >
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    onNodesChange={onNodesChange}
                    onEdgesChange={onEdgesChange}
                    onNodeClick={handleNodeClick}
                    fitView
                    attributionPosition="bottom-right"
                    colorMode={isDarkMode ? 'dark' : 'light'}
                    nodeTypes={{ methodNode: props => <MethodNode {...props} handleDeleteNode={handleDeleteNode} nodesLength={nodes.length} /> }}
                >
                    <Background />
                    <Controls />
                    <MiniMap
                        nodeColor={(node) => {
                            return getMethodColor(node.data.request_type);
                        }}
                    />
                    <Panel position="top-right">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end', marginRight: 4, marginTop: 4 }}>
                            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}>
                                <IconButton
                                    onClick={handleToggleFullscreen}
                                    size="small"
                                    sx={{
                                        color: 'var(--text-secondary, #fff)',
                                        background: 'rgba(0,0,0,0.2)',
                                        '&:hover': {
                                            background: 'var(--hover-bg)',
                                            color: 'var(--text-primary, #fff)'
                                        },
                                        padding: 1,
                                    }}
                                >
                                    {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Add Node">
                                <IconButton
                                    onClick={handleAddNode}
                                    size="small"
                                    sx={{
                                        color: 'var(--text-secondary, #fff)',
                                        background: 'rgba(0,0,0,0.4)',
                                        '&:hover': {
                                            background: "var(--hover-bg)",
                                            color: 'var(--text-primary, #fff)'
                                        },
                                        padding: 1,
                                    }}
                                >
                                    <Plus size={16} />
                                </IconButton>
                            </Tooltip>
                        </div>
                    </Panel>
                </ReactFlow>
                {/* Edit Dialog */}
                <Dialog
                    open={showEditModal}
                    container={containerRef.current}
                    onClose={handleCancelEdit}
                    aria-labelledby="edit-api-dialog-title"
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle
                        sx={{
                            bgcolor: 'var(--background-primary)',
                            borderBottom: '1px solid var(--borderColor)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            Edit API Node
                        </Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCancelEdit}
                            aria-label="close"
                            sx={{ color: 'var(--text-primary)' }}
                        >
                            <X size={16} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ bgcolor: 'var(--background-primary)' }}>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="api-select-label">Select API</InputLabel>
                            <Select
                                labelId="api-select-label"
                                id="api-select"
                                label="Select API"
                                value={editApiId}
                                onChange={e => handleEditApiChange(e.target.value)}
                                MenuProps={{ container: containerRef.current }}
                            >
                                {endpoints.map((api, idx) => (
                                    <MenuItem
                                        key={api.id}
                                        value={api.id.toString()}
                                        disabled={usedApiIds.has(api.id.toString()) && api.id.toString() !== (selectedNode ? selectedNode.id : "")}
                                    >
                                        {api.request_type} - {api.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <TextField
                                id="edit-api-title-input"
                                value={editTitle}
                                onChange={handleEditTitleChange}
                                placeholder="Enter title"
                                variant="outlined"
                                size="small"
                                label="Title"
                                fullWidth
                                sx={{ mt: 1 }}
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: 'var(--background-primary)' }}>
                        <Button
                            variant="outlined"
                            color='error'
                            onClick={handleCancelEdit}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveEdit}
                            color="primary"
                            disabled={!editApiId}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* AddNode Dialog */}
                <Dialog
                    open={showAddNodeModal}
                    onClose={() => setShowAddNodeModal(false)}
                    aria-labelledby="add-api-dialog-title"
                    maxWidth="sm"
                    fullWidth
                    container={containerRef.current}
                >
                    <DialogTitle
                        sx={{
                            bgcolor: 'var(--background-primary)',
                            borderBottom: '1px solid var(--borderColor)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            Add API Node
                        </Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={() => setShowAddNodeModal(false)}
                            aria-label="close"
                            sx={{ color: 'var(--text-primary)' }}
                        >
                            <X size={16} />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ bgcolor: 'var(--background-primary)' }}>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <InputLabel id="add-api-select-label">Select API</InputLabel>
                            <Select
                                labelId="add-api-select-label"
                                id="add-api-select"
                                label="Select API"
                                value={newNodeApiId}
                                onChange={e => setNewNodeApiId(e.target.value)}
                                MenuProps={{ container: containerRef.current }}
                            >
                                {endpoints.map((api, idx) => (
                                    <MenuItem
                                        key={api.id}
                                        value={api.id.toString()}
                                        disabled={usedApiIds.has(api.id.toString())}
                                    >
                                        {api.request_type} - {api.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth sx={{ mt: 2 }}>
                            <TextField
                                id="add-api-title-input"
                                value={newNodeTitle}
                                onChange={e => setNewNodeTitle(e.target.value)}
                                placeholder="Enter title"
                                variant="outlined"
                                label="Title"
                                size="small"
                                fullWidth
                            />
                        </FormControl>
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: 'var(--background-primary)' }}>
                        <Button
                            variant="outlined"
                            color='error'
                            onClick={() => setShowAddNodeModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveNewNode}
                            color="primary"
                            disabled={!newNodeApiId || !newNodeTitle}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
                {/* Delete confirmation dialog */}
                <Dialog
                    open={showDeleteDialog}
                    onClose={() => setShowDeleteDialog(false)}
                    aria-labelledby="delete-node-dialog-title"
                    maxWidth="xs"
                    fullWidth
                    container={containerRef.current}
                >
                    <DialogTitle id="delete-node-dialog-title">Delete Node</DialogTitle>
                    <DialogContent>
                        <Typography>Are you sure you want to delete this node?</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowDeleteDialog(false)} variant="outlined">Cancel</Button>
                        <Button onClick={confirmDeleteNode} color="error" variant="contained">Delete</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </>
    );
};

export default ApiFlowGraph;